/**
 * Supabase client and identity — present only when the build has the two public env vars.
 *
 *   VITE_SUPABASE_URL       https://<ref>.supabase.co
 *   VITE_SUPABASE_ANON_KEY  the publishable anon key (safe in client code; RLS does the work)
 *
 * Without them every export here is null / a no-op and the app behaves exactly as before:
 * telemetry POSTs to /api/telemetry and the service worker answers 202. That gate is what
 * lets this land before the project exists.
 *
 * IDENTITY, LOCAL-MINT-FIRST
 * The first card must never block on the network. The client keeps its own persistent id (the
 * one App.tsx already mints into localStorage) and starts playing immediately; anonymous
 * sign-in runs in the background and, once it lands, rows are written under a real auth.uid()
 * that the server resolves to a durable `actor`. The local id rides along as client_mint_id so
 * pre-auth rows can be joined to the actor afterwards.
 *
 * Anonymous sign-in is rate-limited per IP (30/hour) and carrier NAT puts thousands of phones
 * behind one IP, so a 429 here is expected traffic, not an error: the transport keeps queueing
 * until a session exists.
 */
import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const supabaseConfigured = Boolean(url && anonKey);

export const supabase: SupabaseClient | null = supabaseConfigured
  ? createClient(url!, anonKey!, {
      // detectSessionInUrl: the email-confirmation link lands on the app with the session in
      // the URL fragment; this is what turns that visit into the confirmed account.
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    })
  : null;

/** The minimum surface the resolver needs, so it can be tested with a fake client. */
export interface AuthLike {
  auth: {
    getSession(): Promise<{ data: { session: Session | null } }>;
    signInAnonymously(): Promise<{ data: { session: Session | null }; error: unknown }>;
  };
}

/**
 * Build a session resolver over a client. Concurrent callers share one attempt so two
 * sign-ins cannot race; a SUCCESSFUL attempt is cached for good; a failed one is forgotten
 * so the next caller retries. Never throws.
 *
 * Kept as a factory because the earlier inline version awaited its own promise inside a
 * `finally` and deadlocked after the first failure — which made the transport look like it
 * was correctly re-queueing when it had simply stopped. A resolver you can hand a fake
 * client to is a resolver you can prove retries.
 */
export function createSessionResolver(client: AuthLike): (() => Promise<Session | null>) & { reset(): void } {
  let inflight: Promise<Session | null> | null = null;
  const resolve = () => {
    if (inflight) return inflight;
    const attempt = (async (): Promise<Session | null> => {
      try {
        const { data } = await client.auth.getSession();
        if (data.session) return data.session;
        const { data: signed, error } = await client.auth.signInAnonymously();
        return error ? null : signed.session;
      } catch {
        return null;
      }
    })();
    inflight = attempt.then((session) => {
      if (!session) inflight = null; // forget failure; keep success
      return session;
    });
    return inflight;
  };
  // reset(): forget the cached session. Sign-in and sign-out change who the session is, and
  // a resolver that kept the old answer would keep stamping the old identity.
  return Object.assign(resolve, {
    reset() {
      inflight = null;
    },
  });
}

const resolve = supabase ? createSessionResolver(supabase) : null;

/** Resolve to an authenticated session, signing in anonymously if needed. Null when unconfigured or unavailable. */
export function ensureSession(): Promise<Session | null> {
  return resolve ? resolve() : Promise.resolve(null);
}

/** After sign-in / sign-out: the next ensureSession() re-reads the session instead of the cached one. */
export function resetSession(): void {
  resolve?.reset();
}
